import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { PlenaLogo } from "../components/PlenaLogo";
import { Lock } from "lucide-react";

export default function Login() {
  const { user, login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/workspace"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = challenge ? await verifyMfa(challenge, mfaCode) : await login(email, password);
      if (result.mfa_required) {
        setChallenge(result.challenge_token);
        setPassword("");
        return;
      }
      const user = result;
      navigate(user.role === "admin" ? "/admin" : "/workspace");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#161618] flex flex-col items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#007AFF]/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#30B0C7]/10 blur-3xl" />
      </div>
      <div className="relative w-full max-w-sm animate-fade-up">
        <div className="text-center mb-10">
          <div className="mb-7 flex justify-center">
            <PlenaLogo size="lg" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white font-display">{challenge ? "İki adımlı doğrulama" : "Hesabınıza giriş yapın"}</h1>
          <p className="text-sm text-slate-500 mt-2">{challenge ? "Authenticator kodunuzu veya recovery kodunuzu girin" : "Proje ve task yönetim alanınıza erişin"}</p>
        </div>
        <form onSubmit={submit} className="bg-white/85 dark:bg-[#1C1C1E]/85 backdrop-blur-2xl rounded-2xl border border-white/60 dark:border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 space-y-4">
          {!challenge && <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">E-posta</label>
            <input
              data-testid="login-email-input"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@firma.com"
              className="mt-1.5 w-full h-11 px-4 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all"
            />
          </div>}
          {!challenge && <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Şifre</label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-medium text-[#0066CC] hover:underline">Şifremi unuttum</Link>
            </div>
            <input
              data-testid="login-password-input"
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 w-full h-11 px-4 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all"
            />
          </div>}
          {challenge && <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Doğrulama kodu</label>
            <input data-testid="login-mfa-input" autoFocus autoComplete="one-time-code" required
              value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="123456"
              className="mt-1.5 w-full h-11 px-4 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50" />
          </div>}
          {error && (
            <p data-testid="login-error-message" className="text-sm text-[#FF2D55] bg-[#FF2D55]/5 rounded-lg px-3 py-2">{error}</p>
          )}
          <button
            data-testid="login-submit-button"
            type="submit" disabled={loading}
            className="w-full h-11 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] active:scale-[0.98] text-white text-sm font-semibold transition-all shadow-lg shadow-[#0066CC]/25 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <Lock size={14} />}
            {challenge ? "Doğrula" : "Giriş Yap"}
          </button>
          {challenge && <button type="button" onClick={() => { setChallenge(""); setMfaCode(""); }}
            className="w-full text-xs text-slate-500 hover:text-slate-800 dark:hover:text-white">Farklı hesapla giriş yap</button>}
        </form>
        <p className="text-center text-xs text-slate-400 mt-8 flex items-center justify-center gap-1.5">
          <Lock size={11} /> Plena.pro tarafından yönetilen güvenli alan
        </p>
      </div>
    </div>
  );
}
